import { createSelector } from "reselect";
import { Room, Marker } from "./records";
import { DefaultRootState } from "stores";
import { RoomRecord } from "./records";
import { getUid } from "../app.user/selectors";
import { getHasEditableRole } from "../entities.room.members/selectors";
import {
  getAppState,
  getRoomMemberDirectMessageTabs,
} from "../app.state/selectors";
import i18n from "i18next";
import {
  getActiveRoomItems,
  getRoomItemById,
} from "../entities.room.items/selectors";
import { SelectedDragItem } from "containers/DraggableContext";
import {
  getRoomDeckById,
  getRoomDecks,
} from "../entities.room.decks/selectors";
import { getCharacterById } from "../entities.room.characters/selectors";
import { getRoomDiceById } from "../entities.room.dices/selectors";
import { SelectedObject, SelectType } from "../app.state";
import { UpdateObject } from "../entities.room.histories/records/updateFieldObjects";
import { isWithinRange } from "modules/position";
import { Size } from "containers/Screen/Screen/SelectingArea";
import { isEqual } from "lodash-es";

const getRooms = (state: DefaultRootState) => {
  return state.entities.rooms.entities;
};

export const getCurrentRoom = (state: DefaultRootState) => {
  const roomId = state.app.state.roomId;
  if (!roomId) return;
  return state.entities.rooms.entities[roomId];
};

export const getCurrentRoomIsMonitored = (state: DefaultRootState) => {
  const room = getCurrentRoom(state);
  const hasEditableRole = getHasEditableRole(state);
  if (room == null || !hasEditableRole) {
    return false;
  }
  return room.monitored;
};

export const getCurrentRoomTimer = (state: DefaultRootState) => {
  const roomId = state.app.state.roomId;
  if (!roomId) return emptyRoom.timer;
  return state.entities.rooms.entities[roomId].timer;
};

const emptyRoom: Room = RoomRecord({});
export const getRoomById = (state: DefaultRootState, roomId: string) => {
  return state.entities.rooms.entities[roomId] || emptyRoom;
};

export const getUserRoomIds = (state: DefaultRootState) => {
  const uid = getUid(state);
  if (!uid) return [];
  return state.entities.rooms.idsGroupBy[uid] || [];
};

export const getUserSortedRoomIds = createSelector(
  [getRooms, getUserRoomIds],
  (rooms, ids) => {
    const sortedIds = [...ids];
    sortedIds.sort((id1, id2) => {
      const room1 = rooms[id1];
      const room2 = rooms[id2];
      if (room1.createdAt < room2.createdAt) {
        return 1;
      } else if (room1.createdAt > room2.createdAt) {
        return -1;
      } else {
        return 0;
      }
    });
    return sortedIds;
  }
);

const emptyMarker: Marker = {
  x: 0,
  y: 0,
  z: 1,
  width: 2,
  height: 2,
  angle: 0,
  locked: false,
  freezed: false,
  imageUrl: null,
  text: "",
  clickAction: null,
};
export const getRoomMarkerById = (
  state: DefaultRootState,
  roomId: string,
  markerId: string
): Marker => {
  const room = state.entities.rooms.entities[roomId];
  if (!room || !room.markers) return emptyMarker;
  return room.markers[markerId] || emptyMarker;
};

export const getRoomMarkerExists = (
  state: DefaultRootState,
  roomId: string,
  markerId: string
) => {
  const room = state.entities.rooms.entities[roomId];
  if (!room || !room.markers) return false;
  const marker = room.markers[markerId];
  return marker != null;
};

const getProps = (_: unknown, props: { roomId: string }) => props.roomId;
export const getRoomMarkerIds = createSelector(
  [getRooms, getProps],
  (rooms, roomId) => {
    if (rooms[roomId]) {
      const roomMarkerIds = Object.keys(rooms[roomId].markers || {});
      return roomMarkerIds.sort((a, b) => a.localeCompare(b));
    } else {
      return [];
    }
  }
);

export const getMaxZIndexInMarkers = createSelector(
  [getCurrentRoom],
  (room) => {
    const markers = room?.markers || {};
    let maxZIndex = 1;

    for (let key in markers) {
      if (markers.hasOwnProperty(key)) {
        maxZIndex = Math.max(maxZIndex, markers[key].z);
      }
    }
    return Math.min(999, maxZIndex);
  }
);

export const getIsAppliedExtentionProduct = createSelector(
  [getCurrentRoom, (_, productId: string) => productId],
  (room, productId) => {
    if (room == null) {
      return false;
    }

    return room.appliedExtentionProductIds.includes(productId);
  }
);

const getMessageGroups = (state: DefaultRootState) => {
  const room = getCurrentRoom(state);
  return room?.messageGroups;
};

export const getJoinedMessageGroups = createSelector(
  [getMessageGroups, getUid],
  (groups, uid) => {
    if (!groups || !uid) {
      return [];
    }

    return groups.filter(
      (group) => group.kind === "public" || group.uids.includes(uid)
    );
  }
);

const getChatTab = (state: DefaultRootState) =>
  getAppState(state, "roomChatTab");

export const getCurrentMessageGroup = createSelector(
  [getMessageGroups, getChatTab],
  (gropus, tab) => {
    return gropus?.find(({ id }) => id === tab);
  }
);

export const getCurrentTabName = (state: DefaultRootState) => {
  const tab = getChatTab(state);
  const group = getCurrentMessageGroup(state);
  if (group) {
    return group.name;
  }

  const dmChannels = getRoomMemberDirectMessageTabs(state);
  const dm = dmChannels.find((dm) => dm.key === tab);
  if (dm) {
    return dm.name || i18n.t("匿名さん") || "匿名さん";
  }

  return tab;
};

export const getCurrentRoomField = createSelector([getCurrentRoom], (room) => {
  return {
    fieldWidth: room?.fieldWidth,
    fieldHeight: room?.fieldHeight,
    backgroundUrl: room?.backgroundUrl,
    foregroundUrl: room?.foregroundUrl,
    displayGrid: room?.displayGrid,
    gridSize: room?.gridSize,
    fieldObjectFit: room?.fieldObjectFit,
    mediaName: room?.mediaName,
    mediaType: room?.mediaType,
    mediaUrl: room?.mediaUrl,
    mediaVolume: room?.mediaVolume ?? 0.5,
    mediaRepeat: room?.mediaRepeat,
    soundName: room?.soundName,
    soundUrl: room?.soundUrl,
    soundVolume: room?.soundVolume ?? 0.5,
    soundRepeat: room?.soundRepeat,
    markers: room?.markers || {},
  };
});

export const getSelectedObjectsPosition = (
  state: DefaultRootState,
  objectsList?: SelectedObject[]
) => {
  const selectedObjects = objectsList
    ? objectsList
    : getAppState(state, "selectedObjects");
  const roomId = getAppState(state, "roomId");

  if (!roomId) return [];

  const items: SelectedDragItem[] = [];
  selectedObjects.forEach((si) => {
    switch (si.selectType) {
      case "item":
        const item = getRoomItemById(state, si.id);
        if (item && item.active && !item.locked) {
          items.push({
            ...si,
            data: { x: item?.x || 0, y: item?.y || 0 },
            z: item?.z || 0,
            width: item?.width || 0,
            height: item?.height || 0,
            coverImageUrl: item?.coverImageUrl,
          });
        }
        break;
      case "deck":
        const deck = getRoomDeckById(state, si.id);
        if (deck && !deck.locked) {
          items.push({
            ...si,
            data: { x: deck.x, y: deck.y },
            z: deck.zIndex,
            width: deck.width || 0,
            height: deck.height || 0,
          });
        }
        break;
      case "marker":
        const markerExists = getRoomMarkerExists(state, roomId, si.id);
        const marker = getRoomMarkerById(state, roomId, si.id);
        if (markerExists && !marker.locked) {
          items.push({
            ...si,
            data: { x: marker.x, y: marker.y },
            z: marker.z,
            width: marker.width || 0,
            height: marker.height || 0,
          });
        }
        break;
      case "character":
        const character = getCharacterById(state, si.id);
        if (character._id) {
          items.push({
            ...si,
            data: { x: character.x, y: character.y },
            z: 101,
            width: character.width || 0,
            height: character.height || 0,
          });
        }
        break;
      case "diceItem":
        const dice = getRoomDiceById(state, si.id);
        if (dice._id) {
          items.push({
            ...si,
            data: { x: dice.x, y: dice.y },
            z: 10000,
            width: dice.width || 0,
            height: dice.height || 0,
          });
        }
        break;
    }
  });

  return items;
};

export const getSelectedObjects = (
  state: DefaultRootState,
  objectsList?: SelectedObject[]
) => {
  const selectedObjects = objectsList
    ? objectsList
    : getAppState(state, "selectedObjects");
  const roomId = getAppState(state, "roomId");

  if (!roomId) return [];

  const items: UpdateObject[] = [];
  selectedObjects.forEach((si) => {
    switch (si.selectType) {
      case "item":
        const item = getRoomItemById(state, si.id);
        if (item) {
          items.push({
            selectType: "item",
            id: si.id,
            data: { ...item },
          });
        }
        break;
      case "deck":
        const deck = getRoomDeckById(state, si.id);
        if (deck) {
          items.push({
            selectType: "deck",
            id: si.id,
            data: { ...deck },
          });
        }
        break;
      case "marker":
        const markerExists = getRoomMarkerExists(state, roomId, si.id);
        const marker = getRoomMarkerById(state, roomId, si.id);
        if (markerExists) {
          items.push({
            selectType: "marker",
            id: si.id,
            data: { ...marker },
          });
        }
        break;
      case "character":
        const character = getCharacterById(state, si.id);
        if (character._id) {
          items.push({
            selectType: "character",
            id: si.id,
            data: { ...character },
          });
        }
        break;
      case "diceItem":
        const dice = getRoomDiceById(state, si.id);
        if (dice._id) {
          items.push({
            selectType: "diceItem",
            id: si.id,
            data: { ...dice },
          });
        }
        break;
    }
  });

  return items;
};

type MovableRoomObject = {
  id: string;
  type: SelectType;
  x: number;
  y: number;
  width: number;
  height: number;
};

const getRoomId = (state: DefaultRootState) => getAppState(state, "roomId");

const getMarkers = (state: DefaultRootState) => {
  const roomId = getAppState(state, "roomId");
  if (!roomId) return [];
  return state.entities.rooms.entities[roomId].markers;
};

const getCharacters = (state: DefaultRootState) => {
  return state.entities.roomCharacters.entities;
};

const getDiceItems = (state: DefaultRootState) => {
  return state.entities.roomDices.entities;
};

export const getMovableRoomObjects = createSelector(
  [
    getRoomId,
    getActiveRoomItems,
    getRoomDecks,
    getMarkers,
    getCharacters,
    getDiceItems,
  ],
  (roomId, items, decks, markers, characters, diceItems) => {
    const objects: MovableRoomObject[] = [];
    if (!roomId) return objects;

    items.forEach((item) => {
      if (!item.locked && item._id) {
        objects.push({
          id: item._id,
          type: "item",
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.height,
        });
      }
    });

    Object.entries(decks).forEach(([id, deck]) => {
      if (!deck.locked) {
        objects.push({
          id,
          type: "deck",
          x: deck.x,
          y: deck.y,
          width: deck.width,
          height: deck.height,
        });
      }
    });

    Object.entries(markers).forEach(([id, marker]) => {
      if (!marker.locked) {
        objects.push({
          id,
          type: "marker",
          x: marker.x,
          y: marker.y,
          width: marker.width,
          height: marker.height,
        });
      }
    });

    Object.entries(characters).forEach(([id, character]) => {
      if (character.active) {
        objects.push({
          id,
          type: "character",
          x: character.x,
          y: character.y,
          width: character.width,
          height: character.height,
        });
      }
    });

    Object.entries(diceItems).forEach(([id, dice]) => {
      objects.push({
        id,
        type: "diceItem",
        x: dice.x,
        y: dice.y,
        width: dice.width,
        height: dice.height,
      });
    });

    return objects;
  }
);

const getScale = (state: DefaultRootState) => state.app.state.roomScreenScale;
const getCellSize = (state: DefaultRootState) =>
  state.app.state.roomScreenCellSize;
const getCurrentSelectedObjects = (state: DefaultRootState) =>
  state.app.state.selectedObjects;

export const getSelectingObjects = createSelector(
  [
    getScale,
    getCellSize,
    getMovableRoomObjects,
    getCurrentSelectedObjects,
    (_, centerX: number) => centerX,
    (_, _centerX: number, centerY: number) => centerY,
    (_, _centerX: number, _centerY: number, selectingAreaSize: Size) =>
      selectingAreaSize,
  ],
  (
    scale,
    cellSize,
    objects,
    currentObjects,
    centerX,
    centerY,
    selectingAreaSize
  ) => {
    const selectedObjects = objects.filter((item) => {
      const factor = item.type === "character" ? scale : cellSize * scale;
      const itemRect = {
        top: item.y * factor,
        left: item.x * factor,
        width: item.width * cellSize * scale,
        height: item.height * cellSize * scale,
      };
      const selectingRect = {
        top: selectingAreaSize.top - centerY,
        left: selectingAreaSize.left - centerX,
        width: selectingAreaSize.right - selectingAreaSize.left,
        height: selectingAreaSize.bottom - selectingAreaSize.top,
      };
      return isWithinRange(selectingRect, itemRect);
    });
    const resultObjects = selectedObjects.map((item) => ({
      id: item.id,
      selectType: item.type,
    }));
    if (isEqual(currentObjects, resultObjects)) return null;
    return resultObjects;
  }
);

export const getAllowDuplicate = createSelector([getCurrentRoom], (room) => {
  const hasParentRoomPackage = !!room?.parentRoomPackageId;
  if (!hasParentRoomPackage) {
    // Room Packageから作られていない場合は複製可
    return true;
  }
  // Room Packageから作られている場合は、許可されていれば複製可
  return !!room?.allowDuplicate;
});
